import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import Swal from "../utils/apparifySwal";
import { useCart } from "../context/CartContext";
import { useMessaging } from "../context/MessagingContext";
import { useNavigate } from "react-router-dom";
import { getImageUrl, fetchProduct } from "../services/api";
import ShopImage from "./ShopImage";
import ShopProductNameLink from "./ShopProductNameLink";
import { Store, X, Trash2, MessageCircle, ShoppingBag } from "lucide-react";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import { getShopProductPath } from "../utils/productHelpers";
import { openShopperBusinessConversation } from "../utils/shopperMessaging";
import { ListSkeleton, ModalFormSkeleton } from "./skeletons/ShopperSkeletons";

const CART_SIDEBAR_EXIT_MS = 300;

function VariantEditModal({ item, onClose, onSave }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item?.productId) return;
    setLoading(true);
    fetchProduct(item.productId)
      .then((p) => {
        setProduct(p);
        if (item.variantId && p?.variants?.length) {
          const current = p.variants.find((v) => v.id === item.variantId);
          if (current?.optionValues) {
            setSelectedOptions({ ...current.optionValues });
            setSelectedVariant(current);
          }
        }
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [item]);

  useEffect(() => {
    if (!product?.variants?.length || Object.keys(selectedOptions).length === 0) {
      setSelectedVariant(null);
      return;
    }
    const match = product.variants.find((v) => {
      if (!v.optionValues) return false;
      return Object.entries(selectedOptions).every(
        ([k, val]) => v.optionValues[k]?.toLowerCase() === val?.toLowerCase()
      );
    });
    setSelectedVariant(match || null);
  }, [product, selectedOptions]);

  const handleSelect = useCallback((optName, value) => {
    setSelectedOptions((prev) => ({ ...prev, [optName]: value }));
  }, []);

  const handleSave = async () => {
    if (!selectedVariant) return;
    setSaving(true);
    try {
      await onSave(item, selectedVariant.id);
      onClose();
    } catch {
      /* handled by parent */
    } finally {
      setSaving(false);
    }
  };

  const options = product?.options || [];
  const variants = product?.variants || [];
  const stock = selectedVariant ? (selectedVariant.quantity ?? 0) : null;
  const allSelected = options.length > 0 && options.every((o) => selectedOptions[o.name]);
  const isSameVariant = selectedVariant?.id === item?.variantId;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="variant-editor-title"
        className="flex max-h-[78vh] w-full max-w-sm flex-col overflow-hidden rounded-t-3xl border border-neutral-200 bg-white text-neutral-900 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900 dark:text-white sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-neutral-200 px-5 py-4 dark:border-neutral-800">
          <div className="min-w-0 pr-4">
            <h3 id="variant-editor-title" className="text-sm font-bold uppercase tracking-[0.16em]">
              Edit Variant
            </h3>
            <p className="mt-1 truncate text-xs text-neutral-500 dark:text-neutral-400">
              {item?.name || "Choose another option"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-neutral-200 text-neutral-500 transition hover:border-neutral-400 hover:bg-neutral-100 hover:text-neutral-950 dark:border-neutral-700 dark:hover:border-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-white"
            aria-label="Close variant editor"
          >
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          {loading ? (
            <ModalFormSkeleton label="Loading variant options" />
          ) : !product || options.length === 0 ? (
            <p className="py-5 text-center text-sm text-neutral-500 dark:text-neutral-400">
              No variants available for this product.
            </p>
          ) : (
            <div className="space-y-3.5">
              {options.map((option) => (
                <div key={option.id || option.name}>
                  <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
                    {option.name}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {option.values?.map((val) => {
                      const isSelected = selectedOptions[option.name] === val.value;
                      const isAvailable = variants.some((v) => {
                        const hasVal = v.optionValues?.[option.name]?.toLowerCase() === val.value?.toLowerCase();
                        return hasVal && (v.quantity || 0) > 0;
                      });
                      return (
                        <button
                          key={val.id || val.value}
                          type="button"
                          disabled={!isAvailable}
                          onClick={() => handleSelect(option.name, val.value)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                            isSelected
                              ? "border-neutral-950 bg-neutral-950 text-white dark:border-white dark:bg-white dark:text-neutral-950"
                              : isAvailable
                                ? "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-500 hover:bg-neutral-100 hover:text-neutral-950 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:border-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-white"
                                : "cursor-not-allowed border-neutral-100 bg-neutral-50 text-neutral-300 line-through dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-600"
                          }`}
                        >
                          {val.value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {selectedVariant && (
                <div className="rounded-2xl border border-neutral-200 bg-neutral-50 px-3.5 py-3 text-xs font-medium text-neutral-600 dark:border-neutral-800 dark:bg-neutral-800/50 dark:text-neutral-300">
                  {selectedVariant.title || selectedVariant.sku} — {stock > 0 ? `${stock} in stock` : "Out of stock"}
                  {selectedVariant.price_override_cents > 0 && (
                    <span className="ml-2 font-bold tabular-nums text-neutral-950 dark:text-white">
                      ₱{(selectedVariant.price_override_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-neutral-200 px-5 py-4 dark:border-neutral-800">
          <button
            type="button"
            onClick={handleSave}
            disabled={!allSelected || !selectedVariant || isSameVariant || (stock !== null && stock <= 0) || saving}
            className={`w-full rounded-full px-4 py-3 text-xs font-bold tracking-[0.18em] transition active:scale-[0.98] ${
              !allSelected || !selectedVariant || isSameVariant || (stock !== null && stock <= 0) || saving
                ? "cursor-not-allowed bg-neutral-200 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-600"
                : "bg-neutral-950 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200"
            }`}
          >
            {saving ? "SAVING…" : "SAVE VARIANT"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CartSidebar({ isOpen, onClose }) {
  const { cart, loading: cartLoading, increaseItem, decreaseItem, removeItem, updateVariant, totals, selectedItems, selectedTotals, setItemSelected, setAllSelected, setSellerSelected } = useCart();
  const { startConversation } = useMessaging();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const chatOpenTimeoutRef = useRef(null);
  useBodyScrollLock(isOpen);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedStoreKeys, setSelectedStoreKeys] = useState(() => new Set());
  const [selectAllActive, setSelectAllActive] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectAllActive(false);
    setSelectedStoreKeys(new Set());
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (chatOpenTimeoutRef.current) {
        window.clearTimeout(chatOpenTimeoutRef.current);
      }
    };
  }, []);

  const grouped = useMemo(() => {
    const groups = {};
    cart.forEach((item) => {
      const biz = item.businessName || "Apparel Store";
      if (!groups[biz]) groups[biz] = { businessId: item.businessId, items: [] };
      groups[biz].items.push(item);
    });
    return groups;
  }, [cart]);

  const selectedTotal = selectedTotals?.subtotal || 0;
  const selectedCount = selectedTotals?.count || 0;

  const handleCheckout = () => {
    onClose();
    const keys = selectedItems.map((i) => i.key || i.id);
    navigate("/checkout", { state: { selectedKeys: keys } });
  };

  const handleViewCart = () => {
    onClose();
    navigate("/cart");
  };

  const handleVariantSave = async (item, newVariantId) => {
    await updateVariant(item, newVariantId);
  };

  const getStoreSelectionKey = (businessId, businessName) => (
    businessId != null ? `business:${businessId}` : `name:${businessName || "Apparel Store"}`
  );

  const handleStoreSelection = (businessId, businessName) => {
    const storeKey = getStoreSelectionKey(businessId, businessName);
    const shouldSelect = !selectedStoreKeys.has(storeKey);

    setSelectAllActive(false);
    setSelectedStoreKeys((current) => {
      const next = new Set(current);
      if (shouldSelect) next.add(storeKey);
      else next.delete(storeKey);
      return next;
    });
    setSellerSelected(businessId, shouldSelect);
  };

  const handleItemSelection = (item) => {
    const storeKey = getStoreSelectionKey(item.businessId, item.businessName);
    setSelectAllActive(false);
    setSelectedStoreKeys((current) => {
      if (!current.has(storeKey)) return current;
      const next = new Set(current);
      next.delete(storeKey);
      return next;
    });
    setItemSelected(item, !item.isSelected);
  };

  const handleAllSelection = () => {
    const shouldSelect = !selectAllActive;
    setSelectAllActive(shouldSelect);
    setSelectedStoreKeys(() => (
      shouldSelect
        ? new Set(Object.entries(grouped).map(([businessName, group]) => (
          getStoreSelectionKey(group.businessId, businessName)
        )))
        : new Set()
    ));
    setAllSelected(shouldSelect);
  };

  const handleClearCart = async () => {
    if (selectedItems.length === 0) return;

    const isSingleItem = selectedItems.length === 1;
    const result = await Swal.fire({
      icon: "warning",
      title: isSingleItem ? "Clear selected product?" : "Clear selected products?",
      text: isSingleItem
        ? `${selectedItems[0]?.name || "The selected product"} will be removed from your cart.`
        : `${selectedItems.length} selected products will be removed from your cart.`,
      showCancelButton: true,
      confirmButtonText: isSingleItem ? "CLEAR PRODUCT" : "CLEAR PRODUCTS",
      cancelButtonText: "CANCEL",
      focusCancel: true,
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;
    for (const item of selectedItems) {
      await removeItem(item);
    }
  };

  const handleRemoveItem = async (item) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Remove this item?",
      text: `${item?.name || "This item"} will be removed from your cart.`,
      showCancelButton: true,
      confirmButtonText: "REMOVE",
      cancelButtonText: "CANCEL",
      focusCancel: true,
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;
    await removeItem(item);
  };

  const handleOpenSellerChat = (businessId) => {
    if (!businessId) return;

    if (typeof onClose === "function") {
      onClose();
    }

    if (chatOpenTimeoutRef.current) {
      window.clearTimeout(chatOpenTimeoutRef.current);
    }

    chatOpenTimeoutRef.current = window.setTimeout(() => {
      chatOpenTimeoutRef.current = null;

      void openShopperBusinessConversation({
        businessId,
        startConversation,
      });
    }, CART_SIDEBAR_EXIT_MS);
  };

  const Checkbox = ({ checked, indeterminate, onChange, ariaLabel }) => (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onChange();
      }}
      className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border transition ${
        checked || indeterminate
          ? "border-neutral-950 bg-neutral-950 text-white dark:border-white dark:bg-white dark:text-neutral-950"
          : "border-neutral-300 bg-white text-neutral-950 hover:border-neutral-600 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white dark:hover:border-neutral-300"
      }`}
      aria-label={ariaLabel || (checked ? "Deselect" : "Select")}
    >
      {checked && (
        <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6l3 3 5-5" /></svg>
      )}
      {!checked && indeterminate && (
        <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6h8" /></svg>
      )}
    </button>
  );

  return (
    <>
      {/* overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/55 backdrop-blur-[2px] transition-opacity ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        onClick={onClose}
      />

      <aside
        ref={sidebarRef}
        className={`fixed right-0 top-0 z-50 h-full w-full max-w-[30rem] transform transition-transform duration-300 ease-out
                    border-l border-neutral-200 bg-white text-neutral-800 shadow-2xl
                    dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50
                    ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-5 dark:border-neutral-800">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-bold uppercase tracking-[0.16em] text-neutral-950 dark:text-white">
                Your Cart
                <span className="ml-2 inline-flex min-w-6 items-center justify-center rounded-full bg-neutral-100 px-1.5 py-0.5 align-middle text-[10px] font-semibold tracking-normal text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                  {totals?.itemCount || 0}
                </span>
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-600 transition hover:border-neutral-400 hover:bg-neutral-100 hover:text-neutral-950 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-white"
                aria-label="Close cart"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {cart.length > 0 && (
            <div className="flex items-center justify-between border-b border-neutral-200 bg-white px-6 py-3 dark:border-neutral-800 dark:bg-neutral-900">
              <div className="flex items-center gap-2.5">
                <Checkbox
                  checked={selectAllActive}
                  onChange={handleAllSelection}
                  ariaLabel={selectAllActive ? "Deselect all products" : "Select all products"}
                />
                <button
                  type="button"
                  onClick={handleAllSelection}
                  className="text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-700 transition hover:text-neutral-950 dark:text-neutral-300 dark:hover:text-white"
                >
                  Select All
                </button>
              </div>
              <button
                type="button"
                onClick={() => void handleClearCart()}
                disabled={selectedItems.length === 0}
                className="rounded-lg px-2 py-1 text-[11px] font-semibold tracking-[0.08em] text-rose-600 transition hover:text-rose-500 hover:[text-shadow:0_0_10px_rgba(225,29,72,0.6)] disabled:cursor-not-allowed disabled:text-neutral-300 disabled:hover:[text-shadow:none] dark:text-rose-400 dark:hover:text-rose-300 dark:disabled:text-neutral-700"
              >
                CLEAR
              </button>
            </div>
          )}

          {/* Items - grouped by seller */}
          <div className="flex-1 overflow-y-auto">
            {cartLoading ? (
              <div className="p-4"><ListSkeleton rows={4} label="Loading cart items" /></div>
            ) : cart.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-200">
                  <ShoppingBag size={30} strokeWidth={1.35} aria-hidden="true" />
                </div>
                <p className="text-lg font-bold uppercase tracking-[0.12em] text-neutral-950 dark:text-white">
                  Your cart is empty
                </p>
                <p className="mt-2 max-w-xs text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                  Explore the latest apparel and add your favorites here.
                </p>
                <button
                  type="button"
                  onClick={() => { onClose(); navigate("/products"); }}
                  className="mt-6 rounded-full bg-neutral-950 px-7 py-3 text-xs font-bold uppercase tracking-[0.18em] text-white transition hover:bg-neutral-800 active:scale-[0.98] dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200"
                >
                  Shop Products
                </button>
              </div>
            ) : (
              Object.entries(grouped).map(([bizName, group]) => {
                const storeKey = getStoreSelectionKey(group.businessId, bizName);
                const storeSelected = selectedStoreKeys.has(storeKey);
                return (
                <section key={bizName} className="border-b border-neutral-200 last:border-0 dark:border-neutral-800">
                  {/* Seller header */}
                  <div className="sticky top-0 z-20 flex items-center gap-2.5 border-b border-neutral-100 bg-neutral-50/95 px-6 py-3 backdrop-blur-sm dark:border-neutral-800 dark:bg-neutral-900/95">
                    <Checkbox
                      checked={storeSelected}
                      onChange={() => handleStoreSelection(group.businessId, bizName)}
                      ariaLabel={`${storeSelected ? "Deselect" : "Select"} ${bizName}`}
                    />
                    <Store size={13} className="shrink-0 text-neutral-700 dark:text-neutral-300" />
                    <span className="min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-800 dark:text-neutral-200">
                      {bizName}
                    </span>
                    {group.businessId && (
                      <button
                        type="button"
                        onClick={() => handleOpenSellerChat(group.businessId)}
                        className="flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-2.5 text-[10px] font-semibold text-neutral-700 transition hover:border-neutral-500 hover:bg-neutral-100 hover:text-neutral-950 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:border-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-white"
                        aria-label={`Chat with ${bizName}`}
                      >
                        <MessageCircle size={10} />
                        Chat
                      </button>
                    )}
                  </div>

                  {/* Items for this seller */}
                  {group.items.map((item) => {
                    const productPath = getShopProductPath(item);

                    return (
                    <div
                      key={item.key || item.id}
                      className="group/item flex items-start gap-3.5 border-t border-neutral-100 px-6 py-4 transition-colors first:border-t-0 hover:bg-neutral-50/80 dark:border-neutral-800/60 dark:hover:bg-neutral-800/30"
                    >
                      <div className="flex h-20 shrink-0 items-center">
                        <Checkbox
                          checked={!!item.isSelected}
                          onChange={() => handleItemSelection(item)}
                          ariaLabel={`${item.isSelected ? "Deselect" : "Select"} ${item.name || "product"}`}
                        />
                      </div>
                      {productPath ? (
                        <button
                          type="button"
                          onClick={() => { onClose(); navigate(productPath); }}
                          className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-neutral-100 ring-1 ring-neutral-200 transition-shadow hover:ring-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 dark:bg-neutral-800 dark:ring-neutral-700 dark:hover:ring-neutral-500 dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900"
                          aria-label={`Open ${item.name || "product"}`}
                        >
                          <ShopImage
                            src={item.image ? getImageUrl(item.image) : null}
                            alt={item.name}
                            imgClassName="h-full w-full object-cover transition-transform duration-300 ease-out group-hover/item:scale-105"
                            fallbackClassName="w-full h-full flex items-center justify-center text-[9px] text-neutral-400"
                            fallbackLabel="No image"
                            loading="lazy"
                          />
                        </button>
                      ) : (
                        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-neutral-100 ring-1 ring-neutral-200 dark:bg-neutral-800 dark:ring-neutral-700">
                          <ShopImage
                            src={item.image ? getImageUrl(item.image) : null}
                            alt={item.name}
                            imgClassName="h-full w-full object-cover transition-transform duration-300 ease-out group-hover/item:scale-105"
                            fallbackClassName="w-full h-full flex items-center justify-center text-[9px] text-neutral-400"
                            fallbackLabel="No image"
                            loading="lazy"
                          />
                        </div>
                      )}

                      <div className="flex min-h-20 min-w-0 flex-1 flex-col justify-between gap-1.5">
                        <div className="flex items-start justify-between gap-1.5">
                          <ShopProductNameLink
                            product={item}
                            onClick={onClose}
                            className="line-clamp-2 text-sm font-semibold leading-snug"
                            fallbackClassName="line-clamp-2 text-sm font-semibold leading-snug text-neutral-900 dark:text-neutral-50"
                          >
                            {item.name}
                          </ShopProductNameLink>
                          <button
                            type="button"
                            onClick={() => void handleRemoveItem(item)}
                            className="shrink-0 rounded-full p-1.5 text-rose-500 transition hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/30 dark:hover:text-rose-300"
                            aria-label={`Remove ${item.name || "item"}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>

                        {item.variation && (
                          <button
                            type="button"
                            onClick={() => setEditingItem(item)}
                            className="inline-flex self-start items-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-[10px] font-medium text-neutral-600 transition hover:bg-neutral-200 hover:text-neutral-900 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:hover:text-white"
                          >
                            {item.variation}
                            <svg className="w-2.5 h-2.5 opacity-60" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5l3 3 3-3" /></svg>
                          </button>
                        )}

                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center rounded-full border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900">
                            <button
                              type="button"
                              onClick={() => decreaseItem(item)}
                              disabled={Number(item.quantity || 0) <= 1}
                              className="flex h-8 w-8 items-center justify-center rounded-l-full text-sm leading-none text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-950 disabled:cursor-not-allowed disabled:text-neutral-300 disabled:hover:bg-transparent dark:hover:bg-neutral-700 dark:hover:text-white dark:disabled:text-neutral-700 dark:disabled:hover:bg-transparent"
                              aria-label={`Decrease ${item.name || "item"} quantity`}
                            >
                              −
                            </button>
                            <span className="w-7 select-none text-center text-xs font-semibold tabular-nums">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => increaseItem(item)}
                              className="flex h-8 w-8 items-center justify-center rounded-r-full text-sm leading-none text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-950 dark:hover:bg-neutral-700 dark:hover:text-white"
                              aria-label={`Increase ${item.name || "item"} quantity`}
                            >
                              +
                            </button>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 tabular-nums leading-tight">
                              ₱{(item.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                            {item.quantity > 1 && (
                              <p className="text-[10px] text-neutral-900 dark:text-neutral-100 tabular-nums leading-tight">
                                ₱{Number(item.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )})}
                </section>
              )})
            )}
          </div>

          {/* Footer */}
          {cart.length > 0 && (
            <div className="space-y-4 border-t border-neutral-200 bg-white px-6 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-5 shadow-[0_-12px_32px_rgba(0,0,0,0.06)] dark:border-neutral-800 dark:bg-neutral-900 dark:shadow-[0_-12px_32px_rgba(0,0,0,0.22)]">
              <div className="flex items-end justify-between gap-4">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                  {selectedItems.length > 0
                    ? `${selectedCount} item${selectedCount !== 1 ? "s" : ""} selected`
                    : "Subtotal"}
                </span>
                <span className="text-2xl font-bold tabular-nums tracking-tight text-neutral-950 dark:text-white">
                  ₱{(selectedItems.length > 0 ? selectedTotal : (totals?.subtotal || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>

              <div className="flex flex-col gap-2.5">
                <button
                  onClick={handleViewCart}
                  className="order-2 w-full rounded-full border border-neutral-300 bg-white px-4 py-3 text-xs font-bold tracking-[0.16em] text-neutral-800 transition hover:border-neutral-500 hover:bg-neutral-100 hover:text-neutral-950 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-white"
                >
                  VIEW CART
                </button>
                <button
                  onClick={handleCheckout}
                  disabled={selectedItems.length === 0}
                  className={`order-1 w-full rounded-full px-4 py-3.5 text-xs font-bold tracking-[0.18em] transition active:scale-[0.98] ${
                    selectedItems.length === 0
                      ? "bg-neutral-200 text-neutral-400 cursor-not-allowed dark:bg-neutral-800 dark:text-neutral-600"
                      : "bg-neutral-950 text-white shadow-sm hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 dark:hover:bg-neutral-200"
                  }`}
                >
                  CHECKOUT{selectedCount > 0 ? ` (${selectedCount})` : ""}
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {editingItem && (
        <VariantEditModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={handleVariantSave}
        />
      )}
    </>
  );
}
